import React, { useEffect, useCallback, useState, useRef } from "react";
import { useTheme } from "../../hooks/useTheme";
import { useBackButton } from "../../hooks/useBackButton";
import { useMapControls } from "../../hooks/map/useMapControls";
import { useAppSelector } from "../../hooks/redux";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { AddressModal } from "../AddressModal/AddressModal";
import styles from "./LocationClarification.module.css";
import { PlusIcon, MinusIcon, LayersIcon } from "../icons";
import { ThankYouModal } from "../ThankYouModal/ThankYouModal";
import { apiClient } from "../../api";
import { CreateUserReportRequest } from "../../api/client";
import { YandexMetrika } from "../../utils/yandexMetrika";
import { calculateDistance, formatDistance } from "../../utils/distanceUtils";
import { AnnouncementModal } from "../AnnouncementModal/AnnouncementModal";

interface LocationClarificationProps {
  isOpen: boolean;
  onClose: (data?: { coordinates: [number, number] }) => void;
  markerData?: {
    title: string;
    coordinates: [number, number];
    initialCoordinates?: [number, number]; // Исходные координаты для отслеживания расстояния
    eventId?: number;
    locationId?: number;
    markerType?: "dps" | "clean" | "dtp";
  } | null;
  warmMapRef: React.RefObject<HTMLDivElement | null>;
  warmMapInstance: any;
  isWarmMapReady: boolean;
  onMoveWarmMapToContainer: (container: HTMLDivElement) => void;
  onResetWarmMap: () => void;
}

export const LocationClarification: React.FC<LocationClarificationProps> = ({
  isOpen,
  onClose,
  markerData,
  warmMapInstance,
  isWarmMapReady,
  onMoveWarmMapToContainer,
  onResetWarmMap,
}) => {
  const { theme } = useTheme();
  const { selectedCity, isTrafficVisible: reduxTrafficVisible } =
    useAppSelector((state) => state.map);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  // Состояния для геокодинга и модалок
  const [localTrafficVisible, setLocalTrafficVisible] =
    useState(reduxTrafficVisible);
  const [currentAddress, setCurrentAddress] = useState("");
  const alternativeNameRef = useRef("");
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(true);
  const [lastGeocodedCoords, setLastGeocodedCoords] = useState<
    [number, number] | null
  >(null);
  const [isMarkerLifted, setIsMarkerLifted] = useState(false);
  const markerAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [currentDistance, setCurrentDistance] = useState<number | null>(null);
  const [showCongratulationModal, setShowCongratulationModal] = useState(false);
  const [congratulationText, setCongratulationText] = useState("");

  const geocodingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isGeocodingRef = useRef(false);
  const lastGeocodingRequestRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    setLocalTrafficVisible(reduxTrafficVisible);
  }, [reduxTrafficVisible]);

  // Используем Back Button от Telegram
  useBackButton({
    isOpen,
    onBack: () => {
      onClose();
    },
  });

  const {
    handleZoomIn: mapZoomIn,
    handleZoomOut: mapZoomOut,
    toggleTraffic,
  } = useMapControls(warmMapInstance, isMapReady);

  const handleToggleTraffic = useCallback(() => {
    const newTrafficState = !localTrafficVisible;
    // Обновляем локальное состояние
    setLocalTrafficVisible(newTrafficState);

    // Применяем к карте
    toggleTraffic(newTrafficState);
  }, [toggleTraffic, localTrafficVisible]);

  const getTitle = useCallback(() => {
    if (markerData?.markerType) {
      switch (markerData.markerType) {
        case "dps":
          return "Добавить локацию ДПС";
        case "clean":
          return "Добавить локацию Чисто";
        case "dtp":
          return "Добавить локацию ДТП";
      }
    }
    return "Уточните точку";
  }, [markerData?.markerType]);

  const getSubtitle = useCallback(() => {
    // Показываем расстояние только если есть initialCoordinates (режим уточнения)
    if (markerData?.initialCoordinates && currentDistance !== null) {
      return `Расстояние от метки: ${formatDistance(currentDistance)}`;
    }
    if (markerData?.markerType) {
      return "Укажите точное местоположение";
    }
    return "Карту можно двигать свайпом";
  }, [markerData?.markerType, markerData?.initialCoordinates, currentDistance]);
  // Геокодинг функции
  const geocodeCoordinates = useCallback(
    async (coordinates: [number, number]): Promise<string> => {
      if (isGeocodingRef.current) return "";

      isGeocodingRef.current = true;

      try {
        const [lng, lat] = coordinates;
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=ru`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Формируем адрес в формате "Ул. Название, дом"
        let formattedAddress = "";

        if (data.address) {
          const { house_number, road, street } = data.address;
          const streetName = road || street;

          if (streetName) {
            // Проверяем, начинается ли уже с "ул." или "улица"
            if (
              streetName.toLowerCase().startsWith("улица") ||
              streetName.toLowerCase().startsWith("ул.")
            ) {
              formattedAddress = streetName;
            } else {
              formattedAddress = `Ул. ${streetName}`;
            }

            if (house_number) {
              formattedAddress += `, ${house_number}`;
            }
          } else {
            // Fallback к display_name если нет улицы
            const displayParts = data.display_name?.split(",") || [];
            formattedAddress =
              displayParts[0] || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          }
        } else {
          formattedAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }

        return formattedAddress;
      } catch (error) {
        console.warn("❌ Ошибка геокодинга:", error);
        return `${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}`;
      } finally {
        isGeocodingRef.current = false;
      }
    },
    []
  );

  const setupMapMovementTracking = useCallback(() => {
    if (!warmMapInstance || !isMapReady) return;

    const handleMoveStart = () => {
      HapticFeedback.light();
      setIsMarkerLifted(true);
      if (markerAnimationTimeoutRef.current) {
        clearTimeout(markerAnimationTimeoutRef.current);
      }
    };

    const handleMoveEnd = () => {
      HapticFeedback.light();
      markerAnimationTimeoutRef.current = setTimeout(() => {
        setIsMarkerLifted(false);
      }, 0);

      // Отслеживаем расстояние от исходной точки
      if (markerData?.initialCoordinates) {
        try {
          const currentCenter = warmMapInstance.getCenter();
          const currentCoords: [number, number] = [
            currentCenter[0],
            currentCenter[1],
          ];
          const distance = calculateDistance(
            markerData.initialCoordinates,
            currentCoords
          );

          setCurrentDistance(distance);
        } catch (error) {
          console.warn("❌ Ошибка вычисления расстояния:", error);
          setCurrentDistance(null);
        }
      } else {
        setCurrentDistance(null);
      }
    };

    warmMapInstance.on("movestart", handleMoveStart);
    warmMapInstance.on("moveend", handleMoveEnd);

    return () => {
      warmMapInstance.off("movestart", handleMoveStart);
      warmMapInstance.off("moveend", handleMoveEnd);
    };
  }, [warmMapInstance, isMapReady, markerData]);

  // Обратный геокодинг (поиск координат по адресу)
  const reverseGeocode = useCallback(
    async (address: string): Promise<[number, number] | null> => {
      if (!address.trim()) return null;

      try {
        // Добавляем название города к запросу для более точного поиска
        const cityName = selectedCity?.name || "";
        const searchQuery = cityName ? `${address}, ${cityName}` : address;
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery
          )}&limit=1&accept-language=ru&countrycodes=ru`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const result = data[0];
          const coordinates: [number, number] = [
            parseFloat(result.lon),
            parseFloat(result.lat),
          ];
          return coordinates;
        }
      } catch (error) {
        console.warn("❌ Ошибка обратного геокодинга:", error);
      }

      return null;
    },
    [selectedCity]
  );

  // Запуск live геокодинга при изменении центра карты (только в режиме редактирования)
  const startLiveGeocoding = useCallback(() => {
    if (!warmMapInstance || !isMapReady) {
      return;
    }

    if (geocodingIntervalRef.current) {
      clearInterval(geocodingIntervalRef.current);
    }

    let lastGeocodingTime = 0; // Время последнего геокодинга

    geocodingIntervalRef.current = setInterval(() => {
      if (warmMapInstance && isMapReady && isEditingAddress) {
        try {
          const center = warmMapInstance.getCenter();
          const currentCoords: [number, number] = [center[0], center[1]];

          if (
            !lastGeocodedCoords ||
            Math.abs(currentCoords[0] - lastGeocodedCoords[0]) > 0.00005 ||
            Math.abs(currentCoords[1] - lastGeocodedCoords[1]) > 0.00005
          ) {
            // Сбрасываем предыдущий таймер
            if (debounceTimeoutRef.current) {
              clearTimeout(debounceTimeoutRef.current);
            }

            // Устанавливаем новый таймер
            debounceTimeoutRef.current = setTimeout(async () => {
              // Проверяем, прошла ли секунда с последнего запроса
              const now = Date.now();
              const timeSinceLastGeocode = now - lastGeocodingTime;

              if (timeSinceLastGeocode < 1000) {
                return;
              }

              const actualCoords = warmMapInstance.getCenter();
              const coords: [number, number] = [
                actualCoords[0],
                actualCoords[1],
              ];

              lastGeocodingTime = now;

              const address = await geocodeCoordinates(coords);
              if (address && address !== currentAddress) {
                setCurrentAddress(address);
                setLastGeocodedCoords(coords);
              }
            }, 500);
          }
        } catch (error) {
          console.warn("❌ Ошибка получения центра карты:", error);
        }
      }
    }, 1000);
  }, [
    warmMapInstance,
    isMapReady,
    isEditingAddress,
    lastGeocodedCoords,
    currentAddress,
    geocodeCoordinates,
  ]);

  // Остановка live геокодинга
  const stopLiveGeocoding = useCallback(() => {
    if (geocodingIntervalRef.current) {
      clearInterval(geocodingIntervalRef.current);
      geocodingIntervalRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const handleCloseAddressModal = useCallback(() => {
    setShowAddressModal(false);
    setIsEditingAddress(false);
    stopLiveGeocoding();
  }, [stopLiveGeocoding]);

  const thankYouTimerRef = useRef<NodeJS.Timeout | null>(null);

  // const handleEditAddress = useCallback(() => {
  //   setIsEditingAddress(true);
  // }, []);

  const handleThankYouModalClose = useCallback(() => {
    // Очищаем таймер если он активен
    if (thankYouTimerRef.current) {
      clearTimeout(thankYouTimerRef.current);
      thankYouTimerRef.current = null;
    }

    setShowThankYouModal(false);

    if (warmMapInstance && isMapReady) {
      try {
        const center = warmMapInstance.getCenter();
        const coordinates: [number, number] = [center[0], center[1]];
        onClose({ coordinates });
      } catch (error) {
        console.warn("⚠ Ошибка получения координат при закрытии:", error);
        if (markerData) {
          onClose({ coordinates: markerData.coordinates });
        } else {
          onClose();
        }
      }
    } else {
      onClose();
    }
  }, [warmMapInstance, isMapReady, onClose, markerData]);

  const handleCongratulationModalClose = useCallback(() => {
    setShowCongratulationModal(false);

    if (warmMapInstance && isMapReady) {
      try {
        const center = warmMapInstance.getCenter();
        const coordinates: [number, number] = [center[0], center[1]];
        onClose({ coordinates });
      } catch (error) {
        console.warn("⚠ Ошибка получения координат при закрытии:", error);
        if (markerData) {
          onClose({ coordinates: markerData.coordinates });
        } else {
          onClose();
        }
      }
    } else {
      onClose();
    }
  }, [warmMapInstance, isMapReady, onClose, markerData]);

  const handleConfirmAddress = useCallback(async () => {
    HapticFeedback.success();

    const savedAlternativeName = alternativeNameRef.current;
    const savedCurrentAddress = currentAddress;

    console.log("🎯 handleConfirmAddress вызван:", {
      hasMapInstance: !!warmMapInstance,
      isMapReady,
      locationId: markerData?.locationId,
      markerType: markerData?.markerType,
      comment: savedAlternativeName,
      currentAddress: savedCurrentAddress,
    });

    if (warmMapInstance && isMapReady) {
      try {
        const center = warmMapInstance.getCenter();
        const coordinates: [number, number] = [center[0], center[1]];

        // Проверяем расстояние для показа поздравления
        // ВАЖНО: всегда вычисляем расстояние заново, а не полагаемся на state
        let shouldShowCongratulation = false;
        let actualDistance: number | null = null;

        if (markerData?.initialCoordinates) {
          actualDistance = calculateDistance(
            markerData.initialCoordinates,
            coordinates
          );

          if (actualDistance <= 100) {
            shouldShowCongratulation = true;
            const distanceText = formatDistance(actualDistance);
            setCongratulationText(
              `Спасибо, что делаете карту идеальной❤️\n\nВы уточнили место всего на ${distanceText} — это уже менее 100 метров и круче, чем GPS у большинства телефонов.`
            );
          }
        }

        setShowAddressModal(false);
        setIsEditingAddress(false);
        stopLiveGeocoding();

        console.log("📏 Проверка расстояния:", {
          hasInitialCoords: !!markerData?.initialCoordinates,
          actualDistance,
          shouldShowCongratulation,
          willSendToServer: !shouldShowCongratulation,
        });

        if (shouldShowCongratulation) {
          // Показываем поздравление вместо ThankYouModal
          setShowCongratulationModal(true);
        } else {
          setShowThankYouModal(true);
          thankYouTimerRef.current = setTimeout(() => {
            handleThankYouModalClose();
          }, 5000);
        }

        // 📡 В ФОНЕ отправляем данные (ТОЛЬКО если расстояние > 50м)
        if (!shouldShowCongratulation) {
          (async () => {
          // Для уточнения существующей локации
          if (markerData?.locationId) {
            const requestData = {
              location_id: markerData.locationId,
              longitude: coordinates[0],
              latitude: coordinates[1],
              comment: savedAlternativeName.trim() || "",
            };
            console.log(
              requestData,
              "❤️ Отправка данных на сервер (уточнение) в фоне"
            );

            try {
              await apiClient.reportLocationClarification(requestData);
              console.log("✅ Уточнение успешно отправлено");
            } catch (error) {
              console.error("❌ ОШИБКА API запроса:", error);
            }
          }
          // Для создания новой метки
          else if (markerData?.markerType) {
            console.log("🔄 Делаем полный геокодинг для новой метки в фоне...");

            const markerTypeMap = {
              dps: 1,
              clean: 2,
              dtp: 3,
            };

            // Ждем минимум 1 секунду с последнего запроса геокодинга
            const now = Date.now();
            const timeSinceLastRequest = now - lastGeocodingRequestRef.current;

            if (timeSinceLastRequest < 1000) {
              const waitTime = 1000 - timeSinceLastRequest;
              console.log(`⏳ Ждем ${waitTime}мс перед запросом геокодинга...`);
              await new Promise((resolve) => setTimeout(resolve, waitTime));
            }

            let formattedAddress = savedCurrentAddress;

            try {
              const [lng, lat] = coordinates;

              lastGeocodingRequestRef.current = Date.now();

              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=ru`
              );

              if (response.ok) {
                const data = await response.json();
                console.log("✅ Получен ответ геокодинга:", data);

                if (data.address) {
                  const { house_number, road, street } = data.address;
                  const streetName = road || street;

                  if (streetName) {
                    if (
                      streetName.toLowerCase().startsWith("улица") ||
                      streetName.toLowerCase().startsWith("ул.")
                    ) {
                      formattedAddress = streetName;
                    } else {
                      formattedAddress = `Ул. ${streetName}`;
                    }

                    if (house_number) {
                      formattedAddress += `, ${house_number}`;
                    }
                  } else {
                    const displayParts = data.display_name?.split(",") || [];
                    formattedAddress =
                      displayParts[0] || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                  }
                } else {
                  formattedAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                }

                console.log("✅ Форматированный адрес:", formattedAddress);
              } else {
                console.warn(
                  "⚠️ Не удалось получить данные геокодинга, статус:",
                  response.status
                );
              }
            } catch (error) {
              console.error("❌ Ошибка геокодинга:", error);
            }

            const requestData: CreateUserReportRequest = {
              city_id: selectedCity?.id || 0,
              location_name: formattedAddress,
              comment: savedAlternativeName.trim() || "",
              latitude: coordinates[1],
              longitude: coordinates[0],
              message_type: markerTypeMap[markerData.markerType] as 1 | 2 | 3,
            };

            console.log(
              "🆕 Отправка новой метки (user-report) в фоне:",
              requestData
            );

            try {
              const result = await apiClient.createUserReport(requestData);
              console.log("✅ Метка успешно создана, ответ сервера:", result);

              if (result && result.success) {
                console.log("✅✅ SUCCESS = true, ID метки:", result.data?.id);
                // Отслеживаем установку локации только для ДПС
                if (markerData.markerType === "dps") {
                  YandexMetrika.setDpsLocation();
                }
              } else {
                console.warn("⚠️ SUCCESS = false или отсутствует:", result);
              }
            } catch (error) {
              console.error("❌ ОШИБКА API запроса создания репорта:", error);
              console.error("❌ Детали ошибки:", {
                message:
                  error instanceof Error ? error.message : "Unknown error",
                stack: error instanceof Error ? error.stack : undefined,
              });
            }
          }
          })();
        }
      } catch (error) {
        console.warn("⚠️ Ошибка получения координат:", error);
        if (markerData) {
          onClose({ coordinates: markerData.coordinates });
        } else {
          onClose();
        }
      }
    }
  }, [
    warmMapInstance,
    isMapReady,
    markerData,
    selectedCity,
    currentAddress,
    stopLiveGeocoding,
    handleThankYouModalClose,
    onClose,
  ]);
  const handleAddressChange = useCallback((address: string) => {
    setCurrentAddress(address);
  }, []);

  const handleAlternativeNameChange = useCallback((alternativeName: string) => {
    alternativeNameRef.current = alternativeName;
  }, []);

  const handleAddressInput = useCallback(
    async (address: string) => {
      const coordinates = await reverseGeocode(address);
      if (coordinates && warmMapInstance && isMapReady) {
        warmMapInstance.setCenter(coordinates);
        warmMapInstance.setZoom(16);
      }
    },
    [reverseGeocode, warmMapInstance, isMapReady]
  );

  useEffect(() => {
    if (isOpen && isWarmMapReady && mapContainerRef.current) {
      console.log("📍 Перенос warm map в модалку");
      onMoveWarmMapToContainer(mapContainerRef.current);
      setIsMapReady(true);
    }

    if (!isOpen && isMapReady) {
      if (thankYouTimerRef.current) {
        clearTimeout(thankYouTimerRef.current);
        thankYouTimerRef.current = null;
      }

      console.log("🔄 Возврат warm map в offscreen");
      onResetWarmMap();
      setIsMapReady(false);
      setShowAddressModal(false);
      setIsEditingAddress(false);
      setCurrentAddress("");
      alternativeNameRef.current = "";
      setLastGeocodedCoords(null);
      setLocalTrafficVisible(reduxTrafficVisible);
      setCurrentDistance(null);
      setShowCongratulationModal(false);
      setCongratulationText("");
      stopLiveGeocoding();
    }
  }, [
    isOpen,
    isWarmMapReady,
    isMapReady,
    onMoveWarmMapToContainer,
    onResetWarmMap,
    reduxTrafficVisible,
    stopLiveGeocoding,
  ]);

  // Установка координат маркера и начальный геокодинг
  useEffect(() => {
    if (isMapReady && warmMapInstance && markerData && markerData.coordinates) {
      if (markerData.coordinates[0] !== 0 || markerData.coordinates[1] !== 0) {
        setTimeout(async () => {
          if (warmMapInstance) {
            warmMapInstance.setCenter(markerData.coordinates);
            warmMapInstance.setZoom(16);

            // Сразу показываем модалку в режиме редактирования
            setIsEditingAddress(true);
            setShowAddressModal(true);

            // Геокодинг в фоне
            const address = await geocodeCoordinates(markerData.coordinates);
            if (address) {
              setCurrentAddress(address);
              setLastGeocodedCoords(markerData.coordinates);
            }
          }
        }, 300);
      }
    }
  }, [isMapReady, warmMapInstance, markerData, geocodeCoordinates]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (thankYouTimerRef.current) {
        clearTimeout(thankYouTimerRef.current);
        thankYouTimerRef.current = null;
      }

      stopLiveGeocoding();
    };
  }, [stopLiveGeocoding]);

  useEffect(() => {
    if (isEditingAddress) {
      startLiveGeocoding();
    } else {
      stopLiveGeocoding();
    }
  }, [isEditingAddress, startLiveGeocoding, stopLiveGeocoding]);

  const handleZoomIn = useCallback(() => {
    mapZoomIn();
  }, [mapZoomIn]);

  const handleZoomOut = useCallback(() => {
    mapZoomOut();
  }, [mapZoomOut]);

  useEffect(() => {
    if (!isMapReady || !warmMapInstance || !isEditingAddress) return;

    return setupMapMovementTracking();
  }, [isMapReady, warmMapInstance, isEditingAddress, setupMapMovementTracking]);

  useEffect(() => {
    const getHackNum = () => null as unknown;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLInputElement | HTMLElement;
      if (
        (target && /input|textarea/i.test(target.tagName)) ||
        target.contentEditable === "true"
      ) {
        if (target.dataset.hack) return;
        target.dataset.hack = "1";

        const originalRect = target.getBoundingClientRect.bind(target);
        target.getBoundingClientRect = () => {
          const result = originalRect();
          if (document.activeElement === target) {
            // Приводим null как unknown к number — TS(2352) уйдёт
            return { ...result, top: getHackNum() as number };
          }
          return result;
        };

        // Cleanup на blur
        const handleBlur = () => {
          delete target.dataset.hack;
          target.getBoundingClientRect = originalRect;
        };
        target.addEventListener("blur", handleBlur, { once: true });
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => document.removeEventListener("focusin", handleFocusIn);
  }, []);

  if (!isOpen) return null;

  return (
    <div className={styles.container}>
      {/* Заголовок с градиентом */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{getTitle()}</h1>
          <p className={styles.subtitle}>{getSubtitle()}</p>
        </div>
      </div>

      {/* Контейнер для карты */}
      <div ref={mapContainerRef} className={styles.mapContainer}>
        {/* Спиннер загрузки */}
        {!isMapReady && (
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner} />
          </div>
        )}
        {/* Тень - всегда показана когда карта готова */}
        {isMapReady && <div className={styles.markerShadow} />}
        {/* Маркер в центре (только после загрузки карты) */}
        {isMapReady && (
          <div
            className={`${styles.centerMarker} ${
              isMarkerLifted ? styles.lifted : ""
            }`}
          >
            <div className={styles.markerIcon}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 16 17"
                fill="black"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.125 15.8754V2.75037"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1.125 12.1254C6.125 8.37538 9.875 15.8754 14.875 12.1254V2.75038C9.875 6.50038 6.125 -0.999622 1.125 2.75038"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.markerTail} />
          </div>
        )}
      </div>

      {/* Кнопки управления картой */}
      {isMapReady && (
        <div className={styles.controls}>
          <div
            className={`${styles.zoomGroup} ${
              theme === "dark" ? styles.dark : ""
            }`}
          >
            <button
              className={`${styles.controlButton} ${styles.zoomInButton} ${
                theme === "dark" ? styles.dark : ""
              }`}
              onClick={handleZoomIn}
            >
              <PlusIcon width="24" height="24" />
            </button>
            <button className={styles.controlButton} onClick={handleZoomOut}>
              <MinusIcon width="24" height="24" />
            </button>
          </div>

          <button
            className={`${styles.trafficButton} ${
              theme === "dark" ? styles.dark : ""
            } ${localTrafficVisible ? styles.active : ""}`}
            onClick={handleToggleTraffic}
          >
            <LayersIcon width="24" height="24" />
          </button>
        </div>
      )}

      {/* Модалка с адресом */}
      <AddressModal
        isOpen={showAddressModal}
        isEditing={isEditingAddress}
        address={currentAddress}
        onClose={handleCloseAddressModal}
        // onEdit={handleEditAddress}
        onConfirm={handleConfirmAddress}
        onAddressChange={handleAddressChange}
        onAddressInput={handleAddressInput}
        onAlternativeNameChange={handleAlternativeNameChange}
        isLocationClarification={!!markerData?.locationId}
      />
      {/* Модалка благодарности */}
      <ThankYouModal
        isOpen={showThankYouModal}
        onClose={handleThankYouModalClose}
      />
      {/* Модалка поздравления за точное уточнение */}
      <AnnouncementModal
        isOpen={showCongratulationModal}
        announcement={congratulationText}
        onClose={handleCongratulationModalClose}
      />
    </div>
  );
};
